import * as yup from 'yup';

export const TRACKING_MODE = {
  REGULAR: 'regular',
  ADVANCED: 'advanced',
};

// Patterns and required-ness mirror tracking-ui-radio-modes.html so this screen
// validates exactly what the prototype did.
const patterns = {
  ga4: /^G-[A-Z0-9]+$/,
  adsId: /^[0-9]+$/,
  adsLabel: /^[A-Za-z0-9_-]{5,}$/,
  gtm: /^GTM-[A-Z0-9]+$/,
  meta: /^[0-9]{5,}$/,
  tiktok: /^[A-Za-z0-9_-]{5,}$/,
};

export const defaultTrackingForm = {
  trackingMode: TRACKING_MODE.REGULAR,
  enableGoogle: false,
  ga4: '',
  enableAds: false,
  conversionId: '',
  conversionLabel: '',
  gtm: '',
  enableMeta: false,
  metaPixelId: '',
  enableTikTok: false,
  tiktokPixelId: '',
};

export const isAdvanced = (values) =>
  values?.trackingMode === TRACKING_MODE.ADVANCED;

const orNull = (value) => (value ? value.trim() : null) || null;

export const buildTrackingMetadata = (values, { legacyGtmId = null } = {}) => {
  const advanced = isAdvanced(values);

  return {
    // The prototype hardcodes null here. We carry the existing value through
    // instead so a merchant who still relies on the legacy field as a runtime
    // fallback does not lose tracking the first time this form is saved.
    GTMID: legacyGtmId,
    tracking: {
      mode: advanced ? 'gtm' : 'guided',
      google: {
        ga4MeasurementId:
          !advanced && values.enableGoogle ? orNull(values.ga4) : null,
        ads: {
          conversionId:
            !advanced && values.enableGoogle && values.enableAds
              ? orNull(values.conversionId)
              : null,
          conversionLabel:
            !advanced && values.enableGoogle && values.enableAds
              ? orNull(values.conversionLabel)
              : null,
        },
        gtmContainerId: advanced ? orNull(values.gtm) : null,
      },
      meta: {
        pixelId:
          !advanced && values.enableMeta ? orNull(values.metaPixelId) : null,
      },
      tiktok: {
        pixelId:
          !advanced && values.enableTikTok
            ? orNull(values.tiktokPixelId)
            : null,
      },
      options: {
        directGoogleEvents: !advanced && !!values.enableGoogle,
        directMetaEvents: !advanced && values.enableMeta ? true : null,
        directTikTokEvents: !advanced && values.enableTikTok ? true : null,
        allowAdvancedGtm: advanced,
      },
    },
  };
};

export const parseTrackingMetadata = (metadata) => {
  const tracking = metadata?.tracking;
  const legacyGtmId = metadata?.GTMID || '';

  if (!tracking) {
    // Merchants configured before metadata.tracking existed only have the
    // legacy container id, which is the Advanced setup in new terms.
    return {
      ...defaultTrackingForm,
      trackingMode: legacyGtmId
        ? TRACKING_MODE.ADVANCED
        : TRACKING_MODE.REGULAR,
      gtm: legacyGtmId,
    };
  }

  const google = tracking.google || {};
  const ads = google.ads || {};
  const advanced = tracking.mode === 'gtm';

  return {
    trackingMode: advanced ? TRACKING_MODE.ADVANCED : TRACKING_MODE.REGULAR,
    enableGoogle: !!(
      google.ga4MeasurementId || tracking.options?.directGoogleEvents
    ),
    ga4: google.ga4MeasurementId || '',
    enableAds: !!(ads.conversionId || ads.conversionLabel),
    conversionId: ads.conversionId || '',
    conversionLabel: ads.conversionLabel || '',
    gtm: google.gtmContainerId || legacyGtmId,
    enableMeta: !!tracking.meta?.pixelId,
    metaPixelId: tracking.meta?.pixelId || '',
    enableTikTok: !!tracking.tiktok?.pixelId,
    tiktokPixelId: tracking.tiktok?.pixelId || '',
  };
};

const requiredWhen = (condition, pattern, message) =>
  yup
    .string()
    .when(
      [
        'trackingMode',
        'enableGoogle',
        'enableAds',
        'enableMeta',
        'enableTikTok',
      ],
      {
        is: (...args) => condition(...args),
        then: (schema) =>
          schema.trim().required(message).matches(pattern, message),
        otherwise: (schema) => schema.notRequired(),
      }
    );

const regular = (trackingMode) => trackingMode !== TRACKING_MODE.ADVANCED;

export const trackingSchema = yup.object().shape({
  ga4: requiredWhen(
    (trackingMode, enableGoogle) => regular(trackingMode) && enableGoogle,
    patterns.ga4,
    'website_settings.tracking.ga4_invalid'
  ),
  conversionId: requiredWhen(
    (trackingMode, enableGoogle, enableAds) =>
      regular(trackingMode) && enableGoogle && enableAds,
    patterns.adsId,
    'website_settings.tracking.conversion_id_invalid'
  ),
  conversionLabel: requiredWhen(
    (trackingMode, enableGoogle, enableAds) =>
      regular(trackingMode) && enableGoogle && enableAds,
    patterns.adsLabel,
    'website_settings.tracking.conversion_label_invalid'
  ),
  metaPixelId: requiredWhen(
    (trackingMode, enableGoogle, enableAds, enableMeta) =>
      regular(trackingMode) && enableMeta,
    patterns.meta,
    'website_settings.tracking.meta_invalid'
  ),
  tiktokPixelId: requiredWhen(
    (trackingMode, enableGoogle, enableAds, enableMeta, enableTikTok) =>
      regular(trackingMode) && enableTikTok,
    patterns.tiktok,
    'website_settings.tracking.tiktok_invalid'
  ),
  gtm: yup.string().when('trackingMode', {
    is: TRACKING_MODE.ADVANCED,
    then: (schema) =>
      schema
        .trim()
        .required('website_settings.tracking.gtm_invalid')
        .matches(patterns.gtm, 'website_settings.tracking.gtm_invalid'),
    otherwise: (schema) => schema.notRequired(),
  }),
});
